import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getApiClient } from '../../api/apiClient';

const IncomingInvoicesList = ({ invoices, formatCurrency, formatDate, onPreviewInvoice, onGenerateInvoice }) => {
  const { t } = useLanguage();

  // Fetch individual incoming invoice details
  const fetchIncomingInvoiceDetails = async (invoiceId) => {
    try {
      const client = getApiClient();
      const response = await client.get(`/admin/received/${invoiceId}`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching incoming invoice details:', error);
      throw error;
    }
  };

  const handlePreviewClick = async (invoice) => {
    try {
      // For incoming invoices, fetch detailed data from the API
      const detailedInvoice = await fetchIncomingInvoiceDetails(invoice.id);
      onPreviewInvoice({
        ...detailedInvoice,
        type: 'incoming'
      });
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      // Fallback to basic invoice data if detailed fetch fails
      onPreviewInvoice({
        ...invoice,
        type: 'incoming'
      });
    }
  };

  if (invoices.length === 0) {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">{t('incomingInvoices')}</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {t('totalNumberOfInvoices')}: {invoices.length}
          </p>
        </div>
        <div className="p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('noIncomingInvoices')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('noIncomingInvoicesDescription')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">{t('incomingInvoices')}</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          {t('totalNumberOfInvoices')}: {invoices.length}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('invoiceNumber')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('sellerName')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('issueDate')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('total')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('status')}
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">{t('actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-mono font-medium text-gray-900">
                    {invoice.seriesNumber || invoice.invoiceNr}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{invoice.counterpartyName}</div>
                  {invoice.sellerTaxIdentifier && (
                    <div className="text-sm text-gray-500">
                      {t('taxIdentifier')}: {invoice.sellerTaxIdentifier}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(invoice.invoiceDate)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{formatCurrency(invoice.totalAmount)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    invoice.eInvoiceStatus === 'RECEIVED' || invoice.status === 'RECEIVED'
                      ? 'bg-green-100 text-green-800'
                      : invoice.eInvoiceStatus === 'PENDING' || invoice.status === 'PENDING'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {invoice.eInvoiceStatus || invoice.status || 'RECEIVED'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handlePreviewClick(invoice)}
                    className="text-purple-600 hover:text-purple-900 mr-4"
                  >
                    {t('view')}
                  </button>
                  <button className="text-blue-600 hover:text-blue-900">
                    {t('downloadPdf')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IncomingInvoicesList;
